import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Banner,
  TextContainer,
  Text,
  Badge,
  DataTable,
  Modal,
  FormLayout,
  Checkbox,
  ProgressBar,
  EmptyState,
  TextField,
  ActionList,
  Popover
} from '@shopify/polaris';
import { 
  DeleteMinor, 
  PlayMinor, 
  RefreshMinor, 
  ExportMinor
} from '@shopify/polaris-icons';
import { useAuth } from '../hooks/useAuth';
import { useStore } from '../contexts/StoreContext';
import apiService from '../services/api';

interface TranslationJob {
  id: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'paused_api_error';
  progress: number;
  target_locale: string;
  resource_type: string;
  created_at: string;
  completed_at?: string;
  total_items: number;
  processed_items: number;
  translated_items: number;
  error_message?: string;
  settings?: any;
}

interface ShopLanguage {
  locale: string;
  name: string;
  published: boolean;
}

const MOCK_JOBS: TranslationJob[] = [
  {
    id: 1,
    status: 'completed',
    progress: 100,
    target_locale: 'fr',
    resource_type: 'PRODUCT',
    created_at: '2024-01-15T10:30:00Z',
    completed_at: '2024-01-15T11:45:00Z',
    total_items: 150,
    processed_items: 150,
    translated_items: 150
  },
  {
    id: 2,
    status: 'in_progress',
    progress: 65,
    target_locale: 'es',
    resource_type: 'COLLECTION',
    created_at: '2024-01-15T14:20:00Z',
    total_items: 25,
    processed_items: 16,
    translated_items: 16
  },
  {
    id: 3,
    status: 'pending',
    progress: 0,
    target_locale: 'it',
    resource_type: 'ONLINE_STORE_PAGE',
    created_at: '2024-01-15T16:00:00Z',
    total_items: 8,
    processed_items: 0,
    translated_items: 0
  }
];

const RESOURCE_TYPES = [
  { label: 'Products', value: 'PRODUCT' },
  { label: 'Collections', value: 'COLLECTION' },
  { label: 'Pages', value: 'ONLINE_STORE_PAGE' },
  { label: 'Blog Posts', value: 'ONLINE_STORE_ARTICLE' },
  { label: 'Blogs', value: 'ONLINE_STORE_BLOG' },
  { label: 'Menus', value: 'ONLINE_STORE_MENU' },
  { label: 'Metafields', value: 'METAFIELD' },
  { label: 'Metaobjects', value: 'METAOBJECT' },
  { label: 'Product Options', value: 'PRODUCT_OPTION' },
  { label: 'Product Option Values', value: 'PRODUCT_OPTION_VALUE' },
  { label: 'Selling Plans', value: 'SELLING_PLAN' },
  { label: 'Selling Plan Groups', value: 'SELLING_PLAN_GROUP' },
  { label: 'Shop Policies', value: 'SHOP_POLICY' },
  { label: 'Payment Gateways', value: 'PAYMENT_GATEWAY' },
  { label: 'Delivery Methods', value: 'DELIVERY_METHOD_DEFINITION' },
  { label: 'Packing Slip Templates', value: 'PACKING_SLIP_TEMPLATE' },
  { label: 'Email Templates', value: 'EMAIL_TEMPLATE' },
  { label: 'Filters', value: 'FILTER' },
  { label: 'Links', value: 'LINK' },
  { label: 'Theme', value: 'ONLINE_STORE_THEME' }
];

export const Translations: React.FC = () => {
  const { user: _user } = useAuth();
  const { currentStore } = useStore();
  const [jobs, setJobs] = useState<TranslationJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isUpdating, setIsUpdating] = useState(false);
  const [shopLanguages, setShopLanguages] = useState<ShopLanguage[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [bulkActionPopoverActive, setBulkActionPopoverActive] = useState(false);
  const [isPerformingBulkAction, setIsPerformingBulkAction] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [newJob, setNewJob] = useState({
    name: '',
    targetLanguages: [] as string[],
    resourceTypes: [] as string[],
    includeExisting: true
  });
  // Add a flag to track if the user has manually edited the job name
  const [jobNameEdited, setJobNameEdited] = useState(false);
  
  useEffect(() => {
    loadJobs();
    loadSettings();
  }, []);

  useEffect(() => {
    if (currentStore) {
      loadShopLanguages();
    }
  }, [currentStore]);

  // Set up real-time progress updates for pending and in-progress jobs
  useEffect(() => {
    const interval = setInterval(() => {
      const hasActiveJobs = jobs.some(job => 
        job.status === 'pending' || job.status === 'in_progress'
      );
      
      if (hasActiveJobs) {
        setIsUpdating(true);
        loadJobs().finally(() => {
          setIsUpdating(false);
          setLastUpdate(new Date());
        });
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [jobs]);

  // Load shop languages when modal opens
  useEffect(() => {
    if (showCreateModal) {
      loadShopLanguages();
    }
  }, [showCreateModal]); // Only depend on showCreateModal, not the other variables

  // Helper to auto-generate a job name
  const autoGenerateJobName = (resourceTypes: string[], targetLanguages: string[]) => {
    // Ensure we have valid fallbacks
    const resourceType = resourceTypes && resourceTypes.length > 0 ? resourceTypes[0] : 
      (settings?.selectedResourceTypes && settings.selectedResourceTypes.length > 0 ? settings.selectedResourceTypes[0] : 
       RESOURCE_TYPES[0]?.value || 'PRODUCT');
    
    const language = targetLanguages && targetLanguages.length > 0 ? targetLanguages[0] : 
      (settings?.targetLanguages && settings.targetLanguages.length > 0 ? settings.targetLanguages[0] : 
       (shopLanguages && shopLanguages.length > 0 ? shopLanguages[0].locale : 'en'));
    
    // Get labels with fallbacks
    const resourceLabel = getResourceTypeLabel(resourceType) || 'Products';
    const languageLabel = getLanguageLabel(language) || 'English';
    
    // Ensure we never return "undefined"
    if (!resourceLabel || resourceLabel === 'undefined') {
      return `Products - ${languageLabel}`;
    }
    if (!languageLabel || languageLabel === 'undefined') {
      return `${resourceLabel} - English`;
    }
    
    return `${resourceLabel} - ${languageLabel}`;
  };

  // When the modal opens, ensure newJob is initialized with at least one value and a valid name
  useEffect(() => {
    if (showCreateModal) {
      setNewJob(prev => {
        let targetLanguages = prev.targetLanguages;
        let resourceTypes = prev.resourceTypes;
        if (!targetLanguages || targetLanguages.length === 0) {
          if (settings?.targetLanguages && settings.targetLanguages.length > 0) {
            targetLanguages = [...settings.targetLanguages];
          } else if (shopLanguages && shopLanguages.length > 0) {
            targetLanguages = [shopLanguages[0].locale];
          } else {
            targetLanguages = ['en'];
          }
        }
        if (!resourceTypes || resourceTypes.length === 0) {
          if (settings?.selectedResourceTypes && settings.selectedResourceTypes.length > 0) {
            resourceTypes = [...settings.selectedResourceTypes];
          } else {
            resourceTypes = [RESOURCE_TYPES[0].value];
          }
        }
        // Only auto-generate name if not manually edited
        let name = prev.name;
        if (!jobNameEdited || !name) {
          name = autoGenerateJobName(resourceTypes, targetLanguages);
        }
        return {
          ...prev,
          targetLanguages,
          resourceTypes,
          name,
        };
      });
      setJobNameEdited(false); // Reset edit flag on modal open
    }
  }, [showCreateModal, settings, shopLanguages]);

  // When shopLanguages loads, update the job name if the modal is open and name wasn't manually edited
  useEffect(() => {
    if (showCreateModal && shopLanguages.length > 0 && !jobNameEdited) {
      setNewJob(prev => {
        const targetLanguages = prev.targetLanguages.length > 0 ? prev.targetLanguages : 
          (settings?.targetLanguages && settings.targetLanguages.length > 0 ? settings.targetLanguages : 
           shopLanguages.length > 0 ? [shopLanguages[0].locale] : ['en']);
        
        const resourceTypes = prev.resourceTypes.length > 0 ? prev.resourceTypes : 
          (settings?.selectedResourceTypes && settings.selectedResourceTypes.length > 0 ? settings.selectedResourceTypes : 
           [RESOURCE_TYPES[0].value]);
        
        return {
          ...prev,
          targetLanguages,
          resourceTypes,
          name: autoGenerateJobName(resourceTypes, targetLanguages),
        };
      });
    }
  }, [shopLanguages, showCreateModal, settings, jobNameEdited]);

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getTranslationJobs();
      if (response.data && 'jobs' in response.data) {
        setJobs(response.data.jobs as TranslationJob[]);
      } else {
        console.error('Failed to load jobs:', response.error);
        // Fallback to mock data for now
        setJobs(MOCK_JOBS);
      }
    } catch (error) {
      console.error('Failed to load translation jobs:', error);
      // Fallback to mock data for now
      setJobs(MOCK_JOBS);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await apiService.getSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadShopLanguages = async () => {
    try {
      if (currentStore) {
        const response = await apiService.getStoreLocales(Number(currentStore.id));
        
        if (response.data) {
          let locales: any[] = [];
          
          // Handle different response formats
          if (Array.isArray(response.data)) {
            locales = response.data;
          } else if (response.data['hydra:member']) {
            locales = response.data['hydra:member'];
          } else if (response.data.locales) {
            locales = response.data.locales;
          } else {
            // Direct object format
            locales = Object.keys(response.data).map(key => ({
              locale: key,
              published: response.data[key].published || false
            }));
          }
          
          setShopLanguages(locales);
        }
      }
    } catch (error) {
      console.error('Failed to load shop languages:', error);
    }
  };

  const createJob = async () => {
    setIsLoading(true);
    try {
      if (!currentStore) {
        alert('No store connected. Please connect your Shopify store first.');
        setShowCreateModal(false);
        return;
      }

      await createTranslationJob(currentStore);
    } catch (error) {
      console.error('Error creating job:', error);
      alert('Failed to create translation job. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const createTranslationJob = async (store: any) => {
    // Ensure we have valid data for job creation
    const targetLanguages = newJob.targetLanguages && newJob.targetLanguages.length > 0 
      ? newJob.targetLanguages 
      : (settings?.targetLanguages && settings.targetLanguages.length > 0 
          ? settings.targetLanguages 
          : (shopLanguages && shopLanguages.length > 0 
              ? [shopLanguages[0].locale] 
              : ['en']));
    
    const resourceTypes = newJob.resourceTypes && newJob.resourceTypes.length > 0 
      ? newJob.resourceTypes 
      : (settings?.selectedResourceTypes && settings.selectedResourceTypes.length > 0 
          ? settings.selectedResourceTypes 
          : [RESOURCE_TYPES[0].value]);
    
    // Generate job name - ensure it's never undefined
    let jobName = newJob.name;
    if (!jobName || jobName.trim() === '') {
      const firstResourceType = resourceTypes[0] || RESOURCE_TYPES[0].value;
      const firstLanguage = targetLanguages[0] || 'en';
      const resourceLabel = getResourceTypeLabel(firstResourceType);
      const languageLabel = getLanguageLabel(firstLanguage);
      jobName = `${resourceLabel} - ${languageLabel}`;
    }
    
    try {
      const jobData = {
        store_id: store.id,
        settings: {
          name: jobName,
          target_languages: targetLanguages,
          resource_types: resourceTypes,
          include_existing: newJob.includeExisting
        }
      };
      
      const response = await apiService.createTranslationJob(jobData);
      
      if (response.data) {
        setJobs(prev => [...response.data.jobs, ...prev]);
        setShowCreateModal(false);
        setNewJob({ name: '', targetLanguages: [], resourceTypes: [], includeExisting: true });
        setModalError(null); // Clear any previous errors
      } else {
        setModalError(`Failed to create translation job: ${response.error}`);
      }
    } catch (error) {
      console.error('Failed to create job:', error);
      setModalError('Failed to create translation job. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge tone="success">Completed</Badge>;
      case 'in_progress':
        return <Badge tone="info">In Progress</Badge>;
      case 'pending':
        return <Badge tone="warning">Pending</Badge>;
      case 'failed':
        return <Badge tone="critical">Failed</Badge>;
      case 'paused_api_error':
        return <Badge tone="warning">Paused (API Error)</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getLanguageLabel = (value: string) => {
    if (!value) return 'English';
    
    const language = shopLanguages.find(lang => lang.locale === value);
    if (language && language.name) {
      return language.name;
    }
    
    // Fallback to hardcoded language names
    const languageMap: { [key: string]: string } = {
      'en': 'English',
      'fr': 'French',
      'de': 'German',
      'es': 'Spanish',
      'it': 'Italian',
      'pt': 'Portuguese',
      'nl': 'Dutch',
      'pl': 'Polish',
      'sv': 'Swedish',
      'fi': 'Finnish',
      'da': 'Danish',
      'no': 'Norwegian'
    };
    
    return languageMap[value] || value || 'English';
  };

  const getResourceTypeLabel = (value: string) => {
    if (!value) return 'Products';
    
    const resource = RESOURCE_TYPES.find(res => res.value === value);
    return resource ? resource.label : value || 'Products';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Bulk operations functions
  const handleJobSelection = (jobId: number, checked: boolean) => {
    if (checked) {
      setSelectedJobs(prev => [...prev, jobId]);
    } else {
      setSelectedJobs(prev => prev.filter(id => id !== jobId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedJobs(jobs.map(job => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedJobs.length === 0) return;
    
    setIsPerformingBulkAction(true);
    setBulkActionPopoverActive(false);
    
    try {
      switch (action) {
        case 'retry':
          await apiService.retryTranslationJobs(selectedJobs);
          await loadJobs();
          break;
        case 'delete':
          // Implement delete jobs
          if (confirm(`Are you sure you want to delete ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''}?`)) {
            for (const jobId of selectedJobs) {
              try {
                await apiService.deleteTranslationJob(jobId);
              } catch (error) {
                console.error(`Failed to delete job ${jobId}:`, error);
              }
            }
            await loadJobs(); // Reload jobs after deletion
          }
          break;
        case 'export':
          // TODO: Implement export jobs
          break;
        case 'refresh':
          await loadJobs();
          break;
        default:
          break;
      }
      
      // Clear selection after action
      setSelectedJobs([]);
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsPerformingBulkAction(false);
    }
  };

  const getBulkActionItems = () => {
    const hasFailedJobs = selectedJobs.some(id => 
      jobs.find(job => job.id === id)?.status === 'failed'
    );
    const hasCompletedJobs = selectedJobs.some(id => 
      jobs.find(job => job.id === id)?.status === 'completed'
    );

    return [
      {
        content: 'Refresh Selected',
        icon: RefreshMinor,
        onAction: () => handleBulkAction('refresh')
      },
      ...(hasFailedJobs ? [{
        content: 'Retry Failed Jobs',
        icon: PlayMinor,
        onAction: () => handleBulkAction('retry')
      }] : []),
      ...(hasCompletedJobs ? [{
        content: 'Export Results',
        icon: ExportMinor,
        onAction: () => handleBulkAction('export')
      }] : []),
      {
        content: 'Delete Selected',
        icon: DeleteMinor,
        destructive: true,
        onAction: () => handleBulkAction('delete')
      }
    ];
  };

  const tableRows = jobs.map(job => {
    // Parse settings if it's a string
    let settings = job.settings;
    if (typeof settings === 'string') {
      try {
        settings = JSON.parse(settings);
      } catch (e) {
        settings = {};
      }
    }
    
    // Get target languages from job settings or fallback to single locale
    const targetLanguages = settings?.target_languages || [job.target_locale];
    
    // Use the job's specific resource type, not all resource types from settings
    const resourceTypes = [job.resource_type];
    
    // Calculate progress safely
    const progress = job.total_items > 0 ? (job.processed_items / job.total_items) * 100 : 0;
    
    return [
      <Checkbox
        key={`checkbox-${job.id}`}
        label=""
        checked={selectedJobs.includes(job.id)}
        onChange={(checked) => handleJobSelection(job.id, checked)}
      />,
      <Text variant="bodyMd" fontWeight="semibold" as="span" key={job.id}>
        {settings?.name || `${getResourceTypeLabel(job.resource_type)} - ${getLanguageLabel(job.target_locale)}`}
      </Text>,
      getStatusBadge(job.status),
      <div key={job.id}>
        {targetLanguages.map((lang: string, index: number) => (
          <Badge key={`${job.id}-lang-${index}`} tone="info">
            {getLanguageLabel(lang)}
          </Badge>
        ))}
      </div>,
      <div key={job.id}>
        {resourceTypes.map((resource: string, index: number) => (
          <Badge key={`${job.id}-resource-${index}`} tone="info">
            {getResourceTypeLabel(resource)}
          </Badge>
        ))}
      </div>,
      <div key={job.id}>
        <ProgressBar progress={progress} size="small" />
        <Text variant="bodySm" as="p">
          {job.processed_items} / {job.total_items} items
        </Text>
      </div>,
      formatDate(job.created_at),
      job.completed_at ? formatDate(job.completed_at) : '-'
    ];
  });

  return (
    <Page
      title="Translations"
      primaryAction={{
        content: 'Start New Translation',
        onAction: () => setShowCreateModal(true)
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Translation Jobs
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Monitor and manage your AI-powered translation jobs.
                  </Text>
                </TextContainer>
                
                {/* Bulk Actions */}
                {selectedJobs.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
                    </Text>
                    <Popover
                      active={bulkActionPopoverActive}
                      activator={
                        <Button
                          onClick={() => setBulkActionPopoverActive(!bulkActionPopoverActive)}
                          loading={isPerformingBulkAction}
                          disabled={isPerformingBulkAction}
                        >
                          Bulk Actions
                        </Button>
                      }
                      onClose={() => setBulkActionPopoverActive(false)}
                    >
                      <ActionList
                        items={getBulkActionItems()}
                      />
                    </Popover>
                  </div>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isUpdating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        backgroundColor: '#008060',
                        animation: 'pulse 1.5s infinite'
                      }}></div>
                      <Text variant="bodySm" as="span" tone="subdued">
                        Updating...
                      </Text>
                    </div>
                  )}
                  <Text variant="bodySm" as="span" tone="subdued">
                    Last updated: {lastUpdate.toLocaleTimeString()}
                  </Text>
                </div>
              </div>

              {jobs.length === 0 ? (
                <EmptyState
                  heading="No translation jobs yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Start your first translation job to translate your Shopify content into multiple languages.</p>
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    Start Translation
                  </Button>
                </EmptyState>
              ) : (
                <>
                  {/* Select All Checkbox */}
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      label="Select All"
                      checked={selectedJobs.length === jobs.length && jobs.length > 0}
                      onChange={handleSelectAll}
                    />
                    {selectedJobs.length > 0 && (
                      <Text variant="bodySm" as="span" tone="subdued">
                        ({selectedJobs.length} of {jobs.length} selected)
                      </Text>
                    )}
                  </div>
                  
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={[
                      '',
                      'Job Name',
                      'Status',
                      'Target Languages',
                      'Resource Types',
                      'Progress',
                      'Created',
                      'Completed'
                    ]}
                    rows={tableRows}
                  />
                </>
              )}
            </div>
          </Card>
        </Layout.Section>

        <Modal
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setModalError(null); // Clear error when modal is closed
          }}
          title="Start New Translation Job"
          primaryAction={{
            content: 'Start Translation',
            onAction: createJob,
            loading: isLoading
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowCreateModal(false)
            }
          ]}
        >
          <Modal.Section>
            {modalError && (
              <Banner
                title="Plan Limit Reached"
                tone="warning"
                action={{
                  content: 'Upgrade Plan',
                  onAction: () => {
                    // Navigate to billing page
                    window.location.href = '/billing';
                  }
                }}
                onDismiss={() => setModalError(null)}
              >
                <p>{modalError}</p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="Job Name"
                value={newJob.name}
                onChange={(value) => {
                  setNewJob(prev => ({ ...prev, name: value }));
                  setJobNameEdited(true);
                }}
                placeholder="e.g., Product Translation - French & German"
                helpText="Give your translation job a descriptive name"
                autoComplete="off"
              />

              <div>
                <Text variant="bodyMd" as="p" fontWeight="semibold">
                  Target Languages (select multiple):
                  {settings && (
                    <span style={{ color: '#637381', fontWeight: 'normal' }}>
                      {' '}({settings.targetLanguages?.length || 0} from settings)
                    </span>
                  )}
                </Text>
                <div style={{ marginTop: '8px' }}>
                  {settings?.targetLanguages && settings.targetLanguages.length > 0 ? (
                    settings.targetLanguages
                      .filter((lang: string) => lang !== settings?.baseLanguage) // Filter out base language
                      .map((lang: string) => {
                        const isChecked = newJob.targetLanguages.includes(lang);
                        const shopLang = shopLanguages.find(sl => sl.locale === lang);
                        
                        return (
                          <label key={lang} style={{ display: 'block', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewJob(prev => ({
                                    ...prev,
                                    targetLanguages: [...prev.targetLanguages, lang]
                                  }));
                                } else {
                                  setNewJob(prev => ({
                                    ...prev,
                                    targetLanguages: prev.targetLanguages.filter(l => l !== lang)
                                  }));
                                }
                              }}
                              style={{ marginRight: '8px' }}
                            />
                            {getLanguageLabel(lang)}
                            {shopLang && (
                              <span style={{ color: '#637381', fontSize: '12px', marginLeft: '4px' }}>
                                ({shopLang.name})
                              </span>
                            )}
                          </label>
                        );
                      })
                  ) : (
                    <Text variant="bodyMd" as="p">
                      No target languages configured in settings. Please configure languages in Settings first.
                    </Text>
                  )}
                </div>
              </div>

              <div>
                <Text variant="bodyMd" as="p" fontWeight="semibold">
                  Resource Types (select multiple):
                  {settings && (
                    <span style={{ color: '#637381', fontWeight: 'normal' }}>
                      {' '}({settings.selectedResourceTypes?.length || 0} from settings)
                    </span>
                  )}
                </Text>
                <div style={{ marginTop: '8px' }}>
                  {RESOURCE_TYPES.map(resource => {
                    const isChecked = newJob.resourceTypes.includes(resource.value);
                    const isFromSettings = settings?.selectedResourceTypes?.includes(resource.value);
                    
                    return (
                      <label key={resource.value} style={{ display: 'block', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewJob(prev => ({
                                ...prev,
                                resourceTypes: [...prev.resourceTypes, resource.value]
                              }));
                            } else {
                              setNewJob(prev => ({
                                ...prev,
                                resourceTypes: prev.resourceTypes.filter(r => r !== resource.value)
                              }));
                            }
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {resource.label}
                        {isFromSettings && (
                          <span style={{ color: '#637381', fontSize: '12px', marginLeft: '4px' }}>
                            (from settings)
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <Checkbox
                label="Include existing content"
                checked={newJob.includeExisting}
                onChange={(checked) => setNewJob(prev => ({ ...prev, includeExisting: checked }))}
                helpText="Translate all existing content that hasn't been translated yet"
              />

              {newJob.targetLanguages.length > 0 && (
                <Banner title="Selected Languages" tone="info">
                  <div>
                    {newJob.targetLanguages.map(lang => (
                      <Badge key={lang} tone="info">
                        {getLanguageLabel(lang)}
                      </Badge>
                    ))}
                  </div>
                </Banner>
              )}

              {newJob.resourceTypes.length > 0 && (
                <Banner title="Selected Resources" tone="info">
                  <div>
                    {newJob.resourceTypes.map(resource => (
                      <Badge key={resource} tone="info">
                        {getResourceTypeLabel(resource)}
                      </Badge>
                    ))}
                  </div>
                </Banner>
              )}
            </FormLayout>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}; 